import { useCallback, useContext, useState } from "react";
import { Outlet } from "react-router-dom";
import {
  AppBar as MuiAppBar,
  Box,
  Drawer as MuiDrawer,
  styled,
} from "@mui/material";
import { ThemeContext } from "./casehero/shared/providers/ThemeProvider";
import Sidebar from "./casehero/shared/components/layout/Sidebar";
import Topbar from "./casehero/shared/components/layout/Topbar";
import CommandPalette, {
  useCommandPaletteHotkey,
} from "./casehero/shared/components/layout/CommandPalette";
import {
  ANIMATIONS,
  APPBAR_HEIGHT,
  CONTENT_PADDING,
  DRAWER_WIDTH,
  DRAWER_WIDTH_MINI,
} from "./casehero/shared/styles/layout";

interface ShellProps {
  open: boolean;
}

/**
 * Permanent sidebar that collapses to an icon rail.
 * Surface colours come from the MuiDrawer theme override.
 */
const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})<ShellProps>(({ open }) => {
  const width = open ? DRAWER_WIDTH : DRAWER_WIDTH_MINI;

  return {
    width,
    flexShrink: 0,
    transition: `width ${ANIMATIONS.spring}`,
    "& .MuiDrawer-paper": {
      width,
      overflow: "hidden",
      transition: `width ${ANIMATIONS.spring}`,
    },
  };
});

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<ShellProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  width: `calc(100% - ${open ? DRAWER_WIDTH : DRAWER_WIDTH_MINI}px)`,
  transition: `width ${ANIMATIONS.spring}`,
}));

/**
 * Application shell: collapsible sidebar, sticky top bar, routed content.
 */
const CaseHero = () => {
  const { drawerOpen, toggleDrawer } = useContext(ThemeContext);
  const [searchOpen, setSearchOpen] = useState(false);

  const openSearch = useCallback(() => setSearchOpen(true), []);
  useCommandPaletteHotkey(openSearch);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar position="fixed" open={drawerOpen} elevation={0}>
        <Topbar />
      </AppBar>

      <Drawer variant="permanent" open={drawerOpen}>
        <Sidebar
          open={drawerOpen}
          onToggle={toggleDrawer}
          onOpenSearch={openSearch}
        />
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.default",
        }}
      >
        {/* Spacer for the fixed app bar */}
        <Box sx={{ height: APPBAR_HEIGHT, flexShrink: 0 }} />
        <Box sx={{ p: CONTENT_PADDING, flex: 1, minWidth: 0 }}>
          <Outlet />
        </Box>
      </Box>

      <CommandPalette
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
      />
    </Box>
  );
};

export default CaseHero;
